import React, {useEffect, useRef, useState} from 'react';
import {Avatar, Box, Center, Container, Flex, HStack, Spinner, useToast, VStack} from '@chakra-ui/react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {TextG12, TextG14, TextG16} from '../../components/typography';
import PromptModal from '../../components/Modals/PromptModal';
import {BlockedUser, useBlockedUsers, useUnblockUser} from '../../hooks/useBlockUser';
import {getDisplayName} from '../../utils/nameFormatters';

const UnblockBtn = styled.button`
  padding: 8px 18px;
  border-radius: 20px;
  font-family: Gordita;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  color: #1a1a1a;
  background: white;
  border: 1px solid #d5d5d5;
  &:active {
    transform: scale(0.96);
  }
  &:disabled {
    background: #ececec;
    color: #b0b0b0;
    border-color: #ececec;
    cursor: not-allowed;
    transform: none;
  }
`;

const BlockedUsers = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const {
    blockedUsers,
    isInitialLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBlockedUsers();
  const unblockMutation = useUnblockUser();

  const [pendingUnblock, setPendingUnblock] = useState<BlockedUser | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {threshold: 0.1},
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleConfirmUnblock = async () => {
    if (!pendingUnblock) return;
    const target = pendingUnblock;
    setPendingUnblock(null);
    try {
      await unblockMutation.mutateAsync(target.id);
      toast({
        title: `${getDisplayName(target)} has been unblocked`,
        status: 'info',
        duration: 3000,
      });
    } catch {
      toast({
        title: 'Could not unblock user. Please try again.',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Container p={0} maxW="100%" bg="#FAFAFA" minH="100vh">
      {/* Header */}
      <Flex bg="white" px={4} py={3} align="center" borderBottom="1px solid #F0F0F0">
        <Box as="button" onClick={() => navigate(-1)} p={1} mr={3}>
          <i className="fi fi-rr-arrow-small-left" style={{fontSize: '20px'}} />
        </Box>
        <TextG16 mt="0" fontWeight="700" flex={1}>
          Blocked Users
        </TextG16>
      </Flex>

      {isInitialLoading ? (
        <Center py={12}>
          <Spinner />
        </Center>
      ) : blockedUsers.length === 0 ? (
        <Center px={6} py={16} flexDir="column" textAlign="center">
          <i className="fi fi-rr-ban" style={{fontSize: '40px', color: '#D5D5D5'}} />
          <TextG14 mt={4} fontWeight="600">
            You haven't blocked anyone
          </TextG14>
          <TextG12 mt={2} color="#757D84">
            People you block from chats or profiles will appear here.
          </TextG12>
        </Center>
      ) : (
        <VStack spacing={0} align="stretch">
          {blockedUsers.map((u) => (
            <HStack
              key={u.id}
              w="100%"
              p={4}
              spacing={3}
              bg="white"
              borderBottom="1px solid #F0F0F0"
              align="center">
              <Avatar
                name={u.name}
                src={u.profile_photo_url || undefined}
                size="md"
              />
              <VStack align="flex-start" spacing={0} flex={1} minW={0}>
                <TextG14 mt="0" fontWeight="600" noOfLines={1}>
                  {getDisplayName(u)}
                </TextG14>
                {u.handle && (
                  <TextG12 mt="0" color="#757D84" noOfLines={1}>
                    @{u.handle}
                  </TextG12>
                )}
              </VStack>
              <UnblockBtn
                onClick={() => setPendingUnblock(u)}
                disabled={unblockMutation.isLoading}>
                Unblock
              </UnblockBtn>
            </HStack>
          ))}
          <Box ref={sentinelRef} h="1px" />
          {isFetchingNextPage && (
            <Center py={4}>
              <Spinner size="sm" />
            </Center>
          )}
        </VStack>
      )}

      <PromptModal
        title={pendingUnblock ? `Unblock ${getDisplayName(pendingUnblock)}?` : ''}
        description={
          "They'll be able to see your messages and interact with you on Walkabout again."
        }
        declineText={'Cancel'}
        acceptText={'Unblock'}
        declineCallback={() => setPendingUnblock(null)}
        acceptCallback={() => handleConfirmUnblock()}
        isOpen={!!pendingUnblock}
      />
    </Container>
  );
};

export default BlockedUsers;
